<template>

<div class="vignette-category-pricing-table">
        <div v-if="loading" class="flex justify-center py-16">
            <div
                class="h-10 w-10 animate-spin rounded-full border-4 border-hu-green/30 border-t-hu-green"
                aria-hidden="true"
            />
        </div>
        <p v-else-if="error" class="text-center text-red-600 py-8 text-sm">{{ error }}</p>
        <template v-else-if="vehicleCategoryRows.length">
            <div class="mb-6 mx-auto w-full max-w-4xl">
                <div
                    class="grid grid-cols-2 gap-2 rounded-xl border border-gray-200 bg-white p-2 shadow-sm"
                >
                    <button
                        v-for="row in vehicleCategoryRows"
                        :key="row.id"
                        type="button"
                        class="flex w-full min-h-[48px] items-center justify-start gap-2 rounded-lg px-3 py-2.5 text-left text-xs font-bold leading-snug transition-all sm:px-4 sm:text-sm"
                        :class="
                            isCategoryTabActive(row)
                                ? 'bg-hu-green text-white shadow-sm'
                                : 'cursor-pointer border border-gray-200 bg-white text-gray-800 hover:bg-gray-50'
                        "
                        @click="selectedCategoryId = row.id"
                    >
                        <span
                            v-if="categoryTabIcon(row)"
                            class="material-symbols-outlined shrink-0 text-[20px] leading-none"
                            :class="isCategoryTabActive(row) ? 'text-white' : 'text-gray-900'"
                        >{{ categoryTabIcon(row) }}</span>
                        <span class="min-w-0 flex-1 hyphens-auto">{{ categoryTabLabel(row) }}</span>
                    </button>
                </div>
            </div>

            <div class="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                <table class="w-full min-w-[720px] border-collapse text-left text-sm">
                    <thead>
                        <tr class="bg-hu-green text-white">
                            <th
                                scope="col"
                                class="px-4 py-3.5 text-xs font-bold uppercase tracking-wider sm:px-5 text-nowrap"
                            >
                                {{ colType }}
                            </th>
                            <th
                                scope="col"
                                class="px-4 py-3.5 text-xs font-bold uppercase tracking-wider sm:px-5 text-nowrap"
                            >
                                {{ colPrice }}
                            </th>
                            <th
                                scope="col"
                                class="px-4 py-3.5 text-xs font-bold uppercase tracking-wider sm:px-5 text-nowrap"
                            >
                                {{ colFee }}
                            </th>
                            <th
                                scope="col"
                                class="px-4 py-3.5 text-xs font-bold uppercase tracking-wider sm:px-5 text-nowrap"
                            >
                                {{ colValidity }}
                            </th>
                            <th
                                scope="col"
                                class="px-4 py-3.5 text-center text-xs font-bold uppercase tracking-wider sm:px-5 w-[120px]"
                            >
                                {{ colBuy }}
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                        <tr
                            v-for="line in tableLines"
                            :key="`${currencyDisplayKey}-${line.id}`"
                            class="transition-colors"
                            :class="line.promoted ? 'bg-green-50/70' : 'hover:bg-gray-50/80'"
                        >
                            <td class="px-4 py-4 font-bold text-gray-900 sm:px-5">
                                <span class="inline-flex flex-wrap items-center gap-2">
                                    {{ line.typeLabel }}
                                    <span
                                        v-if="line.showBestValue"
                                        class="inline-flex items-center rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-white"
                                    >
                                        {{ bestValueLabel }}
                                    </span>
                                </span>
                            </td>
                            <td class="px-4 py-4 font-bold text-gray-900 tabular-nums sm:px-5">
                                {{ formatMoney(line.base) }}
                            </td>
                            <td class="px-4 py-4 text-gray-600 tabular-nums sm:px-5">
                                {{ formatMoney(line.fee) }}
                            </td>
                            <td class="px-4 py-4 text-gray-600 sm:px-5 max-w-md">
                                {{ line.validityText }}
                            </td>
                            <td class="px-4 py-4 text-center sm:px-5">
                                <button
                                    type="button"
                                    class="inline-flex min-w-[88px] items-center justify-center rounded-full bg-hu-green px-4 py-2 text-xs font-black uppercase tracking-wide text-white transition-colors hover:bg-hu-green-dark text-nowrap"
                                    @click="onBuy(line)"
                                >
                                    {{ ctaBuy }}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
        <p v-else class="text-gray-500 text-sm py-8 text-center">
            {{ emptyMessage }}
        </p>
    </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { getProductBasePrice, getProductServiceFee } from '../utils/productPricing';

defineOptions({ name: 'VignetteCategoryPricingTable' });

const store = useStore();

const loading = ref(true);
const error = ref(null);
const selectedCategoryId = ref(null);

const texts = computed(() => store.getters.texts || {});
const categorizedProducts = computed(() => store.getters['form/categorizedProducts'] || []);

const storeCurrency = computed(() => store.getters['currency/currentCurrency'] || {});
const currencies = computed(() => store.getters.currencies || []);

const currencySymbol = computed(
    () => storeCurrency.value?.symbol || currencies.value?.[0]?.symbol || '€',
);

const activeConversionRate = computed(() => {
    const c = storeCurrency.value || {};
    return Number(c.conversion ?? c.rate ?? 1) || 1;
});

const currencyDisplayKey = computed(
    () => `${storeCurrency.value?.code || ''}|${activeConversionRate.value}`,
);

const colType = computed(
    () => texts.value['vignette-prices-api-th-type'] ?? 'Type',
);
const colPrice = computed(
    () => texts.value['vignette-prices.blade.php-th-official'] ?? 'Price',
);
const colFee = computed(
    () => texts.value['vignette-prices.blade.php-th-fee'] ?? 'Service fee',
);
const colValidity = computed(
    () => texts.value['vignette-prices-api-th-validity'] ?? 'Validity',
);
const colBuy = computed(() => texts.value['vignette-prices-api-th-buy'] ?? 'Buy');
const ctaBuy = computed(
    () => texts.value['vignette-prices.blade.php-cta-buy'] ?? 'Buy',
);
const bestValueLabel = computed(
    () => texts.value['vignette-prices-api-best-value'] ?? 'Best value',
);
const emptyMessage = computed(
    () =>
        texts.value['vignette-prices-pricing-table-empty']
        ?? texts.value['pricing-unavailable']
        ?? 'No pricing categories available.',
);

function firstNonEmpty(...vals) {
    for (const v of vals) {
        if (v == null) continue;
        const s = typeof v === 'string' ? v.trim() : String(v).trim();
        if (s !== '') return typeof v === 'string' ? v.trim() : v;
    }
    return '';
}

/** Same fallback as `PricingSection` `PRICING_CARD_GLOBAL.buyPathFallback`. */
const BUY_PATH_FALLBACK = '/buy-vignette';

function pricingCmsObjectsFromWindow() {
    if (typeof window === 'undefined') return {};
    const raw = window.__VIGNETTE_PRICES_CMS_OBJECTS__;
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return {};
    return raw;
}

/**
 * Same merge idea as `PricingSection` `pricingSectionObjectsMap`:
 * home section 4 exposes product buy paths under random CMS keys (`product.value`);
 * prices page field often only has layout objects — merge so lookups work on /prices/.
 * Prices-page objects win on key collisions (hero overrides, etc.).
 */
function mergedPricingCmsObjects() {
    let home = {};
    if (typeof window !== 'undefined' && window.__HOME_SECTION_4_PRICING_OBJECTS__ != null) {
      const w = window.__HOME_SECTION_4_PRICING_OBJECTS__;
      if (typeof w === 'object' && !Array.isArray(w)) home = w;
    }
    const prices = pricingCmsObjectsFromWindow();
    return { ...home, ...prices };
}

function buyPathFromCmsObjects(product, objs) {
    if (!product || objs == null || typeof objs !== 'object' || Array.isArray(objs)) return '';
    const key = String(product.value ?? '').trim();
    if (!key) return '';
    const entry = objs[key];
    if (!entry || typeof entry !== 'object') return '';
    const path = entry.path;
    return typeof path === 'string' ? path.trim() : '';
}

function productPathFromApi(p) {
    if (!p || typeof p !== 'object') return '';
    const direct = typeof p.path === 'string' ? p.path.trim() : '';
    const viaTranslated = typeof p.translated?.path === 'string' ? p.translated.path.trim() : '';
    return direct || viaTranslated;
}

/**
 * First category row that has a product for this duration (same order as API / tabs).
 * Used so every vehicle category shares the same buy URL as the home grid for that duration
 * (CMS keys differ per category; paths should match `PricingSection` for the canonical row).
 */
function findCanonicalProductForDuration(durationKey, categoryRows) {
    if (!durationKey || !Array.isArray(categoryRows) || !categoryRows.length) return null;
    for (const row of categoryRows) {
      const list = sortProductsByPageId(categoryProductsEnabled(row));
      const hit = list.find((p) => durationKeyFromProduct(p) === durationKey);
      if (hit) return hit;
    }
    return null;
}

/**
 * Mirrors `PricingSection` `categoryPath`: merged CMS objects → `objects[product.value].path`,
 * then API path. Prefers the canonical (first category) product so all tabs get identical links.
 */
function resolveBuyDestination(product, categoryRows) {
    const objs = mergedPricingCmsObjects();
    const dk = durationKeyFromProduct(product) || '1';
    const ref = findCanonicalProductForDuration(dk, categoryRows);
    const fromRefCms = ref ? buyPathFromCmsObjects(ref, objs) : '';
    const fromRefApi = ref ? productPathFromApi(ref) : '';
    const fromDirectCms = buyPathFromCmsObjects(product, objs);
    const fromDirectApi = productPathFromApi(product);
    const path =
      (fromRefCms || fromRefApi)
      || (fromDirectCms || fromDirectApi)
      || BUY_PATH_FALLBACK;
    return String(path).trim();
}

function convertPrice(value) {
    return Number(value || 0) * activeConversionRate.value;
}

function formatMoney(amount) {
    const n = convertPrice(amount);
    return `${currencySymbol.value} ${new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(n)}`.trim();
}

const vehicleCategoryRows = computed(() =>
    categorizedProducts.value.filter((r) => r?.products?.length),
);

const selectedCategoryRow = computed(() => {
    const rows = vehicleCategoryRows.value;
    if (!rows.length) return null;
    const id = selectedCategoryId.value;
    return rows.find((r) => String(r.id) === String(id)) || rows[0];
});

watch(
    vehicleCategoryRows,
    (rows) => {
        if (!rows?.length) {
            selectedCategoryId.value = null;
            return;
        }
        const ok = rows.some((r) => String(r.id) === String(selectedCategoryId.value));
        if (!ok) selectedCategoryId.value = rows[0].id;
    },
    { immediate: true },
);

function detectDurationBadgeFromTitle(rawTitle) {
    const t = String(rawTitle || '').toLowerCase();
    if (/\b10\s*(?:day[s]?|dagen?|dag|tage)\b/i.test(t)) return { order: 2, badgeLabel: '10' };
    if (
        /\b30\s*(?:day[s]?|dagen?|dag|daagse|tage)\b/i.test(t)
        || /\b1\s*(?:month|maand|monat)\b/i.test(t)
    ) {
        return { order: 3, badgeLabel: '30' };
    }
    if (
        /\b365\s*(?:day[s]?|dagen?|tage)\b/i.test(t)
        || /\b1\s*(?:year|jaar|jahr|jahre)\b/i.test(t)
        || /\b12\s*month\b/i.test(t)
        || /\bannual\b/i.test(t)
    ) {
        return { order: 4, badgeLabel: '365' };
    }
    if (/\b1\s*(?:day|dag|tag)\b/i.test(t)) return { order: 1, badgeLabel: '1' };
    return null;
}

function getBadgeLabelFromTitle(title) {
    return detectDurationBadgeFromTitle(title)?.badgeLabel ?? '';
}

/**
 * When `duration` is null: take the first number from the title that matches a known vignette length
 * (avoids relying only on words like "day"/"Tag"). Skips unrelated numbers (e.g. years 2024).
 */
function durationKeyFromTitleDigits(rawTitle) {
    const t = String(rawTitle || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (!t) return '';
    const tokens = t.match(/\d+/g);
    if (!tokens?.length) return '';
    for (const tok of tokens) {
        const n = parseInt(tok, 10);
        if (n === 1) return '1';
        if (n === 10) return '10';
        if (n === 30) return '30';
        if (n === 365 || n === 366) return '365';
    }
    return '';
}

function durationKeyFromProduct(p) {
    const rawDur = p?.duration;
    if (rawDur != null && rawDur !== '') {
        const d = Number(rawDur);
        if (Number.isFinite(d)) {
            if (d === 1) return '1';
            if (d === 10) return '10';
            if (d === 30) return '30';
            if (d === 365 || d === 366) return '365';
        }
    }
    const t = p?.translated?.title ?? p?.title ?? '';
    const fromWords = getBadgeLabelFromTitle(t);
    if (fromWords) return fromWords;
    return durationKeyFromTitleDigits(t) || '';
}

function categoryProductsEnabled(row) {
    const list = Array.isArray(row?.products) ? row.products : [];
    return list.filter((p) => p && p.enabled !== false);
}

function sortProductsByPageId(products) {
    return [...products].sort((a, b) => {
        const pa = Number(a?.page_id);
        const pb = Number(b?.page_id);
        const aHas = Number.isFinite(pa);
        const bHas = Number.isFinite(pb);
        if (aHas && bHas && pa !== pb) return pa - pb;
        if (aHas !== bHas) return aHas ? -1 : 1;
        return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    });
}

function categoryTabIcon(row) {
    const directIcon = firstNonEmpty(
        row.material_icon,
        row.icon,
        row.translated?.icon,
    );
    const descriptionIcon = firstNonEmpty(
        descriptionPayload(row.translated?.description).icon,
        descriptionPayload(row.description).icon,
    );
    const raw = firstNonEmpty(directIcon, descriptionIcon);
    if (!raw) return '';
    let s = plainText(raw);
    s = s.replace(/^material-symbols-outlined[\s:]/i, '').replace(/['"]/g, '');
    s = s.split(/\r?\n/)[0].trim().split(/\s+/)[0] || '';
    return s ? s.toLowerCase() : '';
}

function plainText(raw) {
    return String(raw ?? '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function descriptionPayload(raw) {
    if (raw == null || raw === '') return { icon: '', title: '', text: '' };

    if (typeof raw === 'object' && !Array.isArray(raw)) {
        return {
            icon: plainText(raw.icon),
            title: plainText(raw.title),
            text: plainText(raw.text),
        };
    }

    const cleaned = plainText(raw);
    if (!cleaned) return { icon: '', title: '', text: '' };

    try {
        const parsed = JSON.parse(cleaned);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return {
                icon: plainText(parsed.icon),
                title: plainText(parsed.title),
                text: plainText(parsed.text),
            };
        }
    } catch {
        /* Not JSON, keep the existing plain-text fallback. */
    }

    return { icon: '', title: '', text: cleaned };
}

function categoryTabLabel(row) {
    return (
        String(row.translated?.title || row.translated?.name || row.title || row.name || '').trim()
        || '\u00A0'
    );
}

function isCategoryTabActive(row) {
    return String(selectedCategoryId.value) === String(row.id);
}

function validityText(product) {
    const fromApi = firstNonEmpty(
        product.translated?.text,
        product.text,
        descriptionPayload(product.translated?.description).text,
        descriptionPayload(product.description).text,
    );
    return plainText(fromApi);
}

/** CMS `product.value` for the “Best value” row per vehicle category (not shared across tabs). */
const BEST_VALUE_PRODUCT_VALUE_BY_CATEGORY_ID = {
    632: 'BwlDoyJU',
    633: 'lUDZyQqt',
    634: 'EOWSiach',
    635: 'rOjv5gaL',
};

function showBestValuePill(p, categoryId) {
    if (p?.is_popular === true || p?.is_popular === 1 || p?.is_popular === '1') return true;
    const expected = BEST_VALUE_PRODUCT_VALUE_BY_CATEGORY_ID[String(categoryId ?? '')];
    if (!expected) return false;
    return String(p?.value ?? '').trim() === expected;
}

const tableLines = computed(() => {
    const row = selectedCategoryRow.value;
    if (!row) return [];
    const list = sortProductsByPageId(categoryProductsEnabled(row));
    return list.map((p) => {
        const durationKey = durationKeyFromProduct(p) || '1';
        const typeLabel = firstNonEmpty(
            p.translated?.title,
            p.title,
            p.name,
            getBadgeLabelFromTitle(p.translated?.title || p.title),
        );
        const base = getProductBasePrice(p);
        const fee = getProductServiceFee(p);
        const best = showBestValuePill(p, row.id);
        return {
            id: p.id,
            raw: p,
            categoryId: row.id,
            typeLabel,
            base,
            fee,
            validityText: validityText(p),
            promoted: best,
            showBestValue: best,
            durationKey,
        };
    });
});

function onBuy(line) {
    const p = line.raw;
    if (!p?.id) return;
    const patch = { product_id: p.id };
    if (line.categoryId != null && line.categoryId !== '') {
        patch.category_id = line.categoryId;
    }
    store.commit('form/SET_FORM_DATA', patch);
    store.dispatch('form/saveDataToLocalStorage');
    window.location.assign(resolveBuyDestination(p, vehicleCategoryRows.value));
}

const currentLang = computed(() => store.getters.lang || null);

watch(currentLang, async () => {
    loading.value = true;
    error.value = null;
    try {
        await store.dispatch('form/loadCategorizedProducts');
    } catch (e) {
        error.value = e?.message || 'Failed to refresh prices.';
    } finally {
        loading.value = false;
    }
});

onMounted(async () => {
    loading.value = true;
    error.value = null;
    try {
        const rows = store.state.form?.categorizedProducts;
        if (!Array.isArray(rows) || !rows.length) {
            await store.dispatch('form/loadInitialData');
        } else {
            await store.dispatch('form/loadCategorizedProducts');
        }
    } catch (e) {
        error.value = e?.message || 'Failed to load prices.';
    } finally {
        loading.value = false;
    }
});
</script>
